import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaArrowRight } from 'react-icons/fa';
import axios from 'axios';
import './News.css';

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchNews();
  }, [currentPage]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/news?page=${currentPage}&limit=9`);
      
      console.log('Загруженные новости:', response.data);
      
      if (response.data.news) {
        setNews(response.data.news);
        setTotalPages(response.data.totalPages);
      } else {
        setNews(response.data);
      }
      
      setError(null);
    } catch (err) {
      console.error('Ошибка загрузки новостей:', err);
      setError('Ошибка загрузки новостей');
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (item) => {
    if (!item.image_url) return 'https://via.placeholder.com/400x250?text=Нет+изображения';
    
    if (item.image_url.startsWith('data:image')) {
      return item.image_url;
    }
    
    if (item.image_url.startsWith('/uploads')) {
      return `http://localhost:5000${item.image_url}`;
    }
    
    if (item.image_url.startsWith('http')) {
      return item.image_url;
    }
    
    return 'https://via.placeholder.com/400x250?text=Ошибка+загрузки';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    try {
      return new Date(dateString).toLocaleDateString('ru-RU', options);
    } catch (e) {
      return dateString;
    }
  };

  if (loading && news.length === 0) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Загрузка новостей...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5 text-center">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={fetchNews}>
          Попробовать снова
        </Button>
      </Container>
    );
  }

  return (
    <div className="news-page bg-light-subtle">
      <section className="news-hero py-5">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="display-4 fw-bold mb-3" style={{ color: '#006186' }}>
                Новости и события
              </h1>
              <div className="header-line mb-4" style={{ backgroundColor: '#006186', width: '80px', height: '5px' }}></div>
              <p className="lead text-muted">
                Будьте в курсе последних новостей и событий Ошского технологического университета
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="py-5">
        {news.length === 0 ? (
          <p className="text-center text-muted">Новостей пока нет</p>
        ) : (
          <>
            <Row className="g-4">
              {news.map((item, index) => (
                <Col key={item.id} md={6} lg={4}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card className="news-card h-100 border-0 shadow-sm">
                      <div className="card-img-wrapper position-relative">
                        <Card.Img
                          variant="top"
                          src={getImageUrl(item)}
                          className="news-img"
                          style={{ height: '200px', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400x250?text=Фото+недоступно';
                          }}
                        />
                        <div className="date-badge position-absolute bottom-0 start-0 m-3 px-3 py-2 bg-dark bg-opacity-75 text-white rounded-pill small">
                          <FaCalendarAlt className="me-1" /> {formatDate(item.date)}
                        </div>
                      </div>
                      <Card.Body className="p-4 d-flex flex-column">
                        <h5 className="news-card-title mb-3" style={{ fontSize: '1.1rem', lineHeight: '1.4' }}>
                          {item.title}
                        </h5>
                        <p className="news-card-desc text-muted flex-grow-1" style={{ fontSize: '0.9rem' }}>
                          {item.description && item.description.length > 120 
                            ? item.description.substring(0, 120) + '...' 
                            : item.description}
                        </p>
                        <Button
                        variant="link"
                        as={Link}
                        to={`/news/${item.id}`}
                        className="p-0 text-primary text-decoration-none fw-bold news-btn"
                        style={{ color: '#006186' }}
                            >
                          Читать далее <FaArrowRight className="ms-2 arrow-icon" />
                        </Button>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>

            {totalPages > 1 && (
              <div className="pagination-container mt-5 d-flex justify-content-center">
                <Button
                  variant="outline-primary"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="mx-2"
                  style={{ borderColor: '#006186', color: '#006186' }}
                >
                  Назад
                </Button>
                <span className="mx-3 align-self-center">
                  Страница {currentPage} из {totalPages}
                </span>
                <Button
                  variant="outline-primary"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="mx-2"
                  style={{ borderColor: '#006186', color: '#006186' }}
                >
                  Вперед
                </Button>
              </div>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default News;